import { useMemo } from "react";
import dayjs from "dayjs";
import { ConfirmModal } from "@/shared/components/confirm-modal";
import { MedicationCard } from "../../../medbox/components/medications/medications-list/card";
import { copayEligiblePotencyCodes, potencyCodeMap } from "@/shared/constants/potency-code";
import { CustomersController, ICustomersInsuranceTestClaimPrescription, ICustomersInsuranceTestClaimPrescriptionAdditionalMessage } from "@/shared/endpoints/customers";
import { useAppDispatch } from "@/shared/hooks/store";
import { memo, useRef, useState } from "react";
import { ITestClaimTransferPrescriptions, ITransferPrescriptions } from "@/shared/types/interfaces/transfer-prescriptions";
import { Box, Button } from "@/shared/ui";
import BinDark from "@/assets/icons/bin-dark.svg";
import { MedicalCardEditQuantity } from "../medical-card-edit-quantity";
import { IMedicationQuantityInitialValues } from "../../../../../../shared/constants/medication-quantity";

import AlertWarningIcon from '@/assets/icons/alert_warning.svg';

import './style.css';
import { getMedicationQuantityLabel } from "@/shared/helpers/medication-quantity";

const MedicalCard = ({
    medication,
    claim,
    onDelete,
    onQuantityChange,
    hasEditQuantity,
    deletionModalText,
    copayAmount = null,
    shouldShowEditQuantityTooltip,
    showCopay,
    copayRequestsExhausted
}: {
    medication: ITestClaimTransferPrescriptions;
    claim?: ICustomersInsuranceTestClaimPrescription;
    onDelete?: () => void;
    onQuantityChange?: (values: IMedicationQuantityInitialValues) => void;
    hasEditQuantity?: boolean;
    deletionModalText?: string;
    copayAmount?: number | null;
    shouldShowEditQuantityTooltip?: boolean;
    showCopay?: boolean;
    copayRequestsExhausted?: boolean;
}) => {
    const dispatch = useAppDispatch();
    const [deleteModalShown, setDeleteModalShown] = useState(false);
    const [editMedication, setEditMedication] = useState<ITransferPrescriptions | null>(null);
    const boxRef = useRef<HTMLDivElement | null>(null);
    const deleteMedication = () => {
        dispatch(CustomersController.removeTransferPrescription(medication)).then(() => {
            onDelete?.();
        });
        setDeleteModalShown(false);
    };

    const fillTooSoon = useMemo(() => {
        const fillTooSoonRejectCode = !!claim?.rejectCodes?.includes('FILL_TOO_SOON');
        if (!fillTooSoonRejectCode) return null;
        let fillTooSoonDate = (
            claim?.additionalMessage as ICustomersInsuranceTestClaimPrescriptionAdditionalMessage
        )?.nextAvailableFillDate;
        if (fillTooSoonDate) {
            fillTooSoonDate = dayjs(fillTooSoonDate, 'YYYYMMDD').format('MM/DD/YYYY');
        }

        return {
            fillTooSoonDate
        }

    }, [claim]);

    const rejectionNotDueToFillTooSoon = useMemo(() => {
        if (!claim?.rejectCodes?.length) {
            return false;
        }
        const noFillTooSoonRejectCode = !claim?.rejectCodes?.includes('FILL_TOO_SOON');
        return noFillTooSoonRejectCode;
    }, [claim]);

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                justifyContent="space-between"
                gap={2}
                borderRadius={4}
                border={'1px solid black'}
                ref={boxRef}
                sx={{
                    backgroundColor: 'transparent',
                    border: '1px solid black',
                    width: '100%',
                    p: '15px 20px !important',
                }}
            >
                <div className="w-full flex flex-wrap justify-between items-end">
                    <div className="w-full flex justify-between">
                        <div className="w-full">
                            <span className="text-[0.9375rem] md:text-[1.0625rem] flex justify-between font-atkinsonBold text-base uppercase text-dark mb-[0.375rem]">
                                {medication.prescribable.name}
                            </span>
                            {copayEligiblePotencyCodes.has(medication.prescribable.potencyCode) && (
                                <span className="font-atkinsonHyperlegible text-[0.875rem] text-dark">
                                    {getMedicationQuantityLabel(medication)}
                                </span>
                            )}
                        </div>

                        <span
                            className="bg-mint-600 rounded-full cursor-pointer flex items-center justify-center min-w-[2.25rem] max-w-[2.25rem] h-[2.25rem] ml-3"
                            onClick={() => setDeleteModalShown(true)}
                        >
                            <img width="13px" className="w-[0.8125rem]" src={BinDark} alt="delete" />
                        </span>
                    </div>

                    {copayEligiblePotencyCodes.has(medication.prescribable.potencyCode) && copayAmount !== null && hasEditQuantity && (
                        <Button
                            variant="outlined"
                            sx={{ height: '39px', mt: '0.9375rem' }}
                            onClick={() => setEditMedication(medication)}
                        >
                            <span className="font-dmSans font-semibold text-[0.9375rem]">Edit Quantity</span>
                        </Button>
                    )}

                    {showCopay && (
                        <>
                            {copayAmount !== null ? (
                                <h3 className="mt-4 animate-appear text-base font-atkinsonBold">
                                    ${`${copayAmount?.toFixed(2)}`}
                                </h3>
                            ) : (
                                <span className="mt-5 md:mt-[0.9375rem] text-dark font-dmSansBold font-bold text-15 rounded-full flex items-center pl-[0.375rem] pr-2.5 py-[0.4375rem] bg-yellow-100">
                                    <img width={17} className="w-[1.0625rem] mr-[0.4375rem]" src={AlertWarningIcon} />
                                    {`${medication?.otc ? 'Price not available' : !claim || rejectionNotDueToFillTooSoon || claim.error || copayRequestsExhausted ? 'Copay not available' : 'Copay not yet available'}`}
                                </span>
                            )}

                            {fillTooSoon?.fillTooSoonDate && !copayRequestsExhausted && (
                                <span className="italic text-14 w-full font-atkinsonHyperlegible text-dark mt-[0.3125rem]">
                                    Refill available {fillTooSoon.fillTooSoonDate}
                                </span>
                            )}
                        </>
                    )}
                </div>
            </Box>

            {/* <Tooltip
                customPosition="bottom-left"
                wrapperClassName="edit-quantity-tooltip"
                hasCloseIcon={false}
                open={showEditQuantityTooltip}
                anchorEl={boxRef.current}
                onClose={() => setShowEditQuantityTooltip(false)}
            >
                <div
                    onClick={() => setShowEditQuantityTooltip(false)}
                    className="p-[5px] cursor-pointer min-w-[1.1875rem] h-[1.1875rem] flex justify-center items-center bg-mint-400 rounded-full"
                >
                    <img src={CloseIcon} alt="" width={10} className="w-full" />
                </div>
                <div className="text-xs ml-[0.5625rem] font-atkinsonHyperlegible">
                    <span className="font-atkinsonBold">Edit Quantity</span>
                    &nbsp;
                    Your copays are billed on a 30-day cycle, but you can adjust this for specific medications if needed.
                </div>
            </Tooltip> */}

            {editMedication && (
                <MedicalCardEditQuantity
                    onClose={() => setEditMedication(null)}
                    onSubmit={value => { onQuantityChange?.(value); setEditMedication(null); }}
                    medication={editMedication}
                />
            )}

            <ConfirmModal
                open={!!deleteModalShown}
                onCancel={() => setDeleteModalShown(false)}
                onConfirm={deleteMedication}
                title={deletionModalText || 'Are you sure you want to remove this medication?'}
                confirmText="Yes, Remove"
                cancelText="Cancel Removal"
            >
                <MedicationCard medication={medication} showActions={false} />
            </ConfirmModal>
        </>
    );
};

export default memo(MedicalCard);